import React, { useEffect } from 'react';
import { ACCOUNT_STATUS, PLATFORM_NAME_BY_PLATFORM_ID } from '@enums';
import { useNotification, useStore } from '@hooks';
import { reaction } from 'mobx';
import { observer } from 'mobx-react';
import { COLORS } from '../../constants';

const getErrorByAccountStatus = (status, platformId) => {
    const ERROR_BY_STATUS_MAP = {
        [ACCOUNT_STATUS.BLOCKED]: {
            message: 'Action cannot be completed due to your account status',
            description: (
                <p>
                    Please{' '}
                    <a style={{ color: COLORS.$info }} href='mailto:support@klickly.com'>
                        contact support
                    </a>{' '}
                    to resolve this issue.
                </p>
            )
        },
        [ACCOUNT_STATUS.SHUTDOWN]: {
            message: 'Action cannot be completed due to your account status',
            description: (
                <p>
                    Please{' '}
                    <a style={{ color: COLORS.$info }} href='mailto:support@klickly.com'>
                        contact support
                    </a>{' '}
                    to resolve this issue.
                </p>
            )
        },
        [ACCOUNT_STATUS.PAUSED]: {
            message: 'Action cannot be completed due to your account status',
            description: 'Please unpause your account and try again'
        },
        [ACCOUNT_STATUS.DISCONNECTED]: {
            message: 'Action cannot be completed due to your account status',
            description: `Please ensure your ${PLATFORM_NAME_BY_PLATFORM_ID[platformId]} connection is up to date`
        }
    };

    return ERROR_BY_STATUS_MAP[status] || null;
};

export const NotificationWrapper = observer(({ children }) => {
    const store = useStore();
    const notification = useNotification();

    useEffect(() => {
        if (!store) return;

        const disposers = [
            reaction(
                () => store.error,
                // eslint-disable-next-line
                (error) => {
                    if (!error) return;

                    if (error.description === 'Invalid Account Status') {
                        const { status, platformId } = store.accountsStore.currentAccount || {};
                        const errData = getErrorByAccountStatus(status, platformId);
                        if (errData) {
                            notification.error(errData);
                        }
                    } else {
                        notification.error({
                            message: <div data-e2e='common_notification-error_message'>{error.title}</div>,
                            description: <div data-e2e='common_notification-error_description'>{error.description}</div>
                        });
                    }
                }
            ),
            reaction(
                () => store.success,
                (success) => {
                    if (!success) return;

                    notification.success({
                        message: <div data-e2e='common_notification-success_message'>{success.title || 'Success'}</div>,
                        description: (
                            <div data-e2e='common_notification-success_description'>{success.description || ''}</div>
                        )
                    });
                }
            )
        ];

        return () => {
            disposers.forEach((dispose) => dispose());
        };
    }, [store, notification]);

    return <React.Fragment>{children}</React.Fragment>;
});
